/**
 * PM Entity System (epic gh#1867) v1.5 PR-1 — the `pm_type_def.data_schema`
 * DIALECT.
 *
 * ── WHY THIS MOVED INTO `@elmes/shared` ──────────────────────────────────────
 * It was declared in `packages/api/src/modules/pm/pm.schemas.ts` while exactly
 * ONE surface consumed it (`validatePmItemData`). v1.5 gives it three, in two
 * packages:
 *   - the API still VALIDATES items against it;
 *   - the SCHEMA-EVOLUTION CLASSIFIER (`pmSchemaEvolution.ts`, next to this file)
 *     has to reason about the document STRUCTURALLY — "was this field's type
 *     narrowed?", "was this enum closed?" — which is only meaningful against the
 *     closed dialect, not against an opaque bag;
 *   - the settings EDITOR (v1.5 PR-3) renders the structural form from it, which
 *     is the only reason a form-based editor is expressible at all (locked
 *     decision V7).
 *
 * Two packages agreeing on one closed document is precisely the situation
 * `pmCardLayout.ts` already resolved the same way, for the same reason, in the
 * same directory: derive it ONCE. The alternative — a second `.strict()` copy on
 * the shared side — is the [[feedback_cross_package_wire_discriminator_single_source]]
 * shape: a coordinated rename would pass every suite while the two halves stopped
 * describing the same document. `pmCardLayout`'s own header already had to name
 * this schema as "the authority on that document's full shape" from across the
 * package boundary; now it can point at a symbol instead of at prose.
 *
 * ── WHAT THE DIALECT IS ──────────────────────────────────────────────────────
 * A CLOSED description of the type-specific fields an item's `data` JSONB may
 * carry. `.strict()` at both levels: an unknown key in a field spec, or in the
 * document itself, is a REJECT rather than a silently-ignored annotation. That
 * closedness is what makes v1.5 possible at all — a structural classifier and a
 * structural form both need to enumerate the document, and neither can enumerate
 * an open record.
 *
 * K16 lives one level up, in the COLUMN: SQL `NULL` means "no contract authored"
 * and `{}` means "authored, declaring nothing". This schema describes only the
 * second state; the first has no document to parse. See
 * `pmCardLayout.ts`'s `PmWireDataSchema` for the type that carries the
 * distinction across the wire.
 *
 * REQUIREDNESS IS NOT HERE. `pm_type_def.required_fields` is its own column, so
 * expressing it a second time inside `data_schema` would create two sources of
 * truth for one rule. This schema describes TYPES; the column describes PRESENCE.
 */
import { z } from 'zod';
/**
 * The closed set of scalar types a declared `data` field may take.
 *
 * A NAMED, EXPORTED tuple rather than an inline `z.enum([...])` literal, because
 * v1.5 has two consumers that must enumerate it at RUNTIME and cannot read a zod
 * internal: the evolution classifier (which reports "`string` → `integer`" as a
 * type change) and PR-3's field-type picker (which lists the choices to an
 * operator). An inline literal would have forced each of them to restate the
 * list — three copies of a closed set, held together by nothing.
 */
export const PM_DATA_FIELD_TYPES = ['string', 'number', 'integer', 'boolean'];
/**
 * RESOURCE BOUNDS on the document — not semantic rules.
 *
 * `data_schema` is the ONE caller-supplied structure on the new write path that
 * is walked on EVERY subsequent item write (`validatePmItemData` iterates
 * `fields`, and each declared `enum` is scanned per value), and both its before
 * and after images are copied into the `pm_type_def_event.changes` jsonb on every
 * edit. An unbounded field count or an unbounded value set therefore buys
 * unbounded work per item write and unbounded audit rows, from a single request.
 *
 * The same reasoning already bounds `ai_brief_template` (65536) in the service;
 * leaving the document those bounds are about unbounded would have made them
 * decorative.
 */
export const PM_DATA_SCHEMA_MAX_FIELDS = 128;
/**
 * Upper bound on `pm_type_def.required_fields`. A resource bound, not a semantic
 * rule: the column is unbounded jsonb and a caller should not be able to make
 * every future item validation walk an arbitrarily long list.
 *
 * ── WHY IT LIVES HERE, NEXT TO THE FIELD CAP, AND NOT IN THE API SERVICE ─────
 * The two numbers are not independent. `required_fields ⊆ data_schema.fields` is
 * a CROSS-FIELD RULE the update service enforces, so a required-fields cap ABOVE
 * the field cap describes a contract that cannot legally exist: an author who
 * supplies 200 required fields is refused by the field cap, not by this one.
 *
 * It was declared in `packages/api/.../pmTypeDef.service.ts` while the field cap
 * was declared here, and the agreement between them was pinned in ONE DIRECTION
 * ONLY — a `PM_DATA_SCHEMA_MAX_FIELDS >= 128` literal in this package's test.
 * `@elmes/shared` cannot import `@elmes/api`, so nothing on the shared side could
 * name the other symbol, and nothing on the API side compared them at all.
 * Raising the API constant to 256 therefore turned NOTHING red while making the
 * larger value unreachable — the exact defect the literal pin was written to
 * prevent, arriving through the door the pin could not watch
 * ([[feedback_cross_package_wire_discriminator_single_source]], numeric-bound
 * variant). With both constants in one module the relation is expressible as a
 * relation, and the test below states it as one.
 */
export const PM_REQUIRED_FIELDS_MAX = 128;
export const PM_DATA_FIELD_ENUM_MAX_VALUES = 256;
export const PM_DATA_FIELD_ENUM_MAX_VALUE_CHARS = 256;
/** Upper bound for a declared `maxLength` (W5-4); a resource bound like the two above. */
export const PM_DATA_FIELD_MAX_LENGTH_CAP = 65536;
/**
 * One declared field: its scalar type, plus an OPTIONAL closed value set.
 *
 * `.strict()` — a spec carrying `required: true` is a REJECT, not a hint that is
 * quietly dropped. Requiredness is `required_fields`' business (module header),
 * and silently ignoring a key an author plainly meant is how a type_def comes to
 * claim a rule it does not enforce.
 *
 * The value set is bounded AND required to be DUPLICATE-FREE. Duplicates carry no
 * meaning (`includes` cannot care), and admitting them makes a length-then-
 * membership comparison report `['a','a'] → ['a']` as a value-set CHANGE — a
 * fabricated "breaking" verdict for an edit that changes nothing about which
 * values validate.
 */
export const pmDataFieldSpecSchema = z
    .object({
    type: z.enum(PM_DATA_FIELD_TYPES),
    /** Optional closed value set, checked after the type check. */
    enum: z
        .array(z.union([z.string().max(PM_DATA_FIELD_ENUM_MAX_VALUE_CHARS), z.number()]))
        .min(1)
        .max(PM_DATA_FIELD_ENUM_MAX_VALUES)
        .refine((values) => new Set(values.map((v) => `${typeof v}:${String(v)}`)).size === values.length, { message: 'enum values must be unique (a duplicate admits nothing extra)' })
        .optional(),
    /** W5-4: string length cap in UTF-16 units (JS `.length`, the data-note rule's unit); string fields only. */
    maxLength: z.number().int().min(1).max(PM_DATA_FIELD_MAX_LENGTH_CAP).optional(),
})
    .strict()
    .refine((spec) => spec.maxLength === undefined || spec.type === 'string', {
    message: 'maxLength applies to string fields only',
    path: ['maxLength'],
});
/**
 * The whole `data_schema` document.
 *
 * `fields` defaults to `{}` and `additionalFields` defaults to CLOSED, so an
 * authored-but-empty contract means "items of this type carry NO data fields at
 * all" — a real, enforceable statement rather than an absence.
 */
export const pmTypeDataSchemaSchema = z
    .object({
    fields: z
        .record(z.string().min(1).max(128), pmDataFieldSpecSchema)
        // `.refine`, because `z.record` has no `.max()`: the key SCHEMA bounds each
        // key's length, which says nothing about HOW MANY there are.
        .refine((fields) => Object.keys(fields).length <= PM_DATA_SCHEMA_MAX_FIELDS, {
        message: `data_schema may declare at most ${PM_DATA_SCHEMA_MAX_FIELDS} fields`,
    })
        .default({}),
    /** Unknown `data` keys: DEFAULT FALSE = reject (fail-closed). */
    additionalFields: z.boolean().default(false),
})
    .strict();
//# sourceMappingURL=pmDataSchema.js.map